/** ⌘K opens the hub on the display with the pointer (spec §8); each display window opens its own. */
import { useEffect } from "react";
import { emit } from "@tauri-apps/api/event";
import {
  openGlobalSearch,
  type GlobalSearchRequest,
} from "@/lib/global-search";
import { isTauri } from "@/lib/is-tauri";
import { listenWhileMounted } from "./tauri-listener-cleanup";
import { useActiveDisplay } from "./use-active-display";
import { restoreAndFocusCurrentWindow } from "./use-global-hotkey";
import { getWindowDisplayId } from "./use-window-role";

export const SEARCH_HUB_OPEN_EVENT = "denker://search-hub-open";

interface SearchHubDisplayRequest {
  displayId: number;
  request: GlobalSearchRequest;
}

/** The pointer's display when it is not this window's; null to open here. */
export function searchHubTargetDisplay(): number | null {
  if (!isTauri()) return null;
  const pointerDisplay = useActiveDisplay.getState().activeDisplayId;
  return pointerDisplay !== null && pointerDisplay !== getWindowDisplayId()
    ? pointerDisplay
    : null;
}

export function openSearchHubOnDisplay(
  displayId: number,
  request: GlobalSearchRequest,
): void {
  void emit(SEARCH_HUB_OPEN_EVENT, {
    displayId,
    request,
  } satisfies SearchHubDisplayRequest).catch(() => {});
}

/** Mounted once per display window (DesktopShell): opens the hub when a request names this display. */
export function useSearchHubDisplayRequests(): void {
  useEffect(() => {
    if (!isTauri()) return;
    return listenWhileMounted<SearchHubDisplayRequest>(
      SEARCH_HUB_OPEN_EVENT,
      ({ payload }) => {
        if (payload.displayId !== getWindowDisplayId()) return;
        restoreAndFocusCurrentWindow();
        openGlobalSearch(payload.request);
      },
    );
  }, []);
}
